import { useQuery } from '@tanstack/react-query'
import { Flex, Tabs } from 'antd'
import { UserPlus, UserRound, UsersRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom'

import {
  organizationsJoinRequestsListOptions,
  OrganizationJoinRequestStatusEnum
} from 'src/api/main'
import { TabCountTag } from 'src/components'
import { useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router'
import { normalizeListResults } from 'src/utils'
import { WorkspacePermissionCode } from 'src/utils/workspacePermissions'

import { useWorkspaceUserGroups, useWorkspaceUsersList } from '../hooks'

type TabKey = 'users' | 'groups' | 'joinRequests'

const ICON_SIZE = 14

const COUNT_QUERY = { per_page: 1 } as const

export const WorkspaceMembersTabs = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id: idParam = '' } = useParams<{ id: string }>()
  const workspaceId = Number(idParam)

  const tab: TabKey = location.pathname.endsWith('/groups')
    ? 'groups'
    : location.pathname.endsWith('/join-requests')
      ? 'joinRequests'
      : 'users'

  const setTab = (next: string) => {
    if (next === 'groups') {
      navigate(paths.workspaces.membersGroups(idParam))

      return
    }
    navigate(
      next === 'joinRequests'
        ? paths.workspaces.membersJoinRequests(idParam)
        : paths.workspaces.members(idParam)
    )
  }

  /**
   * Join requests are an administrator's screen — the endpoint refuses anyone
   * else — so the tab is offered on the same tier the sidebar uses to decide
   * who sees the workspace's own settings. Without the gate the count query
   * alone would 403 on every member's visit to Users.
   */
  const [canReviewJoinRequests, canViewGroups, canViewUsers] =
    useHasWorkspacePermissions([
      WorkspacePermissionCode.USERS_INVITE,
      WorkspacePermissionCode.GROUPS_VIEW,
      WorkspacePermissionCode.USERS_VIEW
    ])

  const usersCount = useWorkspaceUsersList({
    workspaceId,
    query: COUNT_QUERY,
    enabled: canViewUsers
  })
  // Groups carry their own codes, so the tab — and its count query, which
  // would 403 — are offered only to a member holding `groups.view`.
  const groupsCount = useWorkspaceUserGroups({
    workspaceId,
    query: COUNT_QUERY,
    enabled: canViewGroups
  })

  // Only what is still waiting on somebody: an approved request is a decision
  // already taken, and counting it would keep a badge on the tab forever.
  const joinRequestsCount = useQuery({
    ...organizationsJoinRequestsListOptions({
      path: { organization_id: workspaceId },
      query: {
        ...COUNT_QUERY,
        status: OrganizationJoinRequestStatusEnum.PENDING
      }
    }),
    enabled: canReviewJoinRequests && Number.isFinite(workspaceId)
  })

  if (!Number.isFinite(workspaceId) || workspaceId <= 0) return null

  // `groups.view` alone opens this section, but not its default tab — send such
  // a member to the one tab they can actually read.
  if (tab === 'users' && !canViewUsers && canViewGroups) {
    return <Navigate to={paths.workspaces.membersGroups(idParam)} replace />
  }

  const usersTotal = usersCount.data?.count
  const groupsTotal = groupsCount.data?.count
  // Annotated as paginated, served as a bare array. `per_page: 1` caps the
  // array too, so a bare-array response can only ever say "at least one" — the
  // badge then shows 1 rather than lying with a bigger number it does not have.
  const joinRequestsData = joinRequestsCount.data
  const pendingJoinRequests = Array.isArray(joinRequestsData)
    ? normalizeListResults(joinRequestsData).length
    : joinRequestsData?.count

  return (
    <Flex vertical gap={14}>
      <Tabs
        activeKey={tab}
        onChange={setTab}
        tabBarStyle={{ marginBottom: 0 }}
        items={[
          ...(canViewUsers
            ? [
                {
                  key: 'users',
                  label: (
                    <Flex align="center" gap={6}>
                      <UserRound size={ICON_SIZE} />
                      {t('users.tabs.people')}
                      {usersTotal == null ? null : (
                        <TabCountTag value={usersTotal} />
                      )}
                    </Flex>
                  )
                }
              ]
            : []),
          ...(canViewGroups
            ? [
                {
                  key: 'groups',
                  label: (
                    <Flex align="center" gap={6}>
                      <UsersRound size={ICON_SIZE} />
                      {t('users.tabs.groups')}
                      {groupsTotal == null ? null : (
                        <TabCountTag value={groupsTotal} />
                      )}
                    </Flex>
                  )
                }
              ]
            : []),
          ...(canReviewJoinRequests
            ? [
                {
                  key: 'joinRequests',
                  label: (
                    <Flex align="center" gap={6}>
                      <UserPlus size={ICON_SIZE} />
                      {t('users.tabs.joinRequests')}
                      {pendingJoinRequests ? (
                        <TabCountTag value={pendingJoinRequests} />
                      ) : null}
                    </Flex>
                  )
                }
              ]
            : [])
        ]}
      />

      <Outlet />
    </Flex>
  )
}
